import { useState, useEffect, useCallback } from 'react'
import { getObraConfig, getLancamentos, getAlertas, getDiario } from '@services/crud.js'
import { db } from '@services/db.js'
import { MODULOS, MODULO_TOTAL_TOPICOS, TOTAL_TOPICOS, topicoDetailPath } from '@utils/constants.js'
import { getDicaDoDia, type Dica } from '@utils/dicas.js'
import type { ObraConfig } from '@services/db.js'
import modulosData from '@content/modulos.json'

export type SaudeFinanceira = 'ok' | 'atencao' | 'critico'

export interface ModuloProgresso {
  id: string
  titulo: string
  icone: string
  concluidos: number
  total: number
  pct: number
}

export interface AtividadeItem {
  tipo: 'lancamento' | 'diario'
  descricao: string
  contexto: string
  data: string
}

export interface ProximoPassoInfo {
  moduloId: string
  moduloTitulo: string
  moduloIcone: string
  topicoId: string | null
  topicoTitulo: string | null
  topicoPath: string | null
  porque: string
  texto: string
}

export interface DashboardData {
  config: ObraConfig | null
  progressoGeral: number
  progressoPorModulo: ModuloProgresso[]
  totalGasto: number
  saldo: number
  pctUsado: number
  saudeFinanceira: SaudeFinanceira
  proximoPasso: string | null
  proximoPassoInfo: ProximoPassoInfo | null
  dica: Dica
  atividadeRecente: AtividadeItem[]
  totalAlertas: number
}

const PORQUE_POR_MODULO: Record<string, string> = {
  terreno:
    'Definir terreno e sondagem antes de qualquer fundação evita 70% dos retrabalhos estruturais.',
  'sistemas-construtivos':
    'Escolher o sistema construtivo antes de fechar projeto evita retrabalho e economiza em média 18%.',
  projetos:
    'Compatibilizar arquitetura, estrutura e hidráulica agora evita quebra de parede durante a execução.',
  'planejamento-financeiro':
    'Distribuir o orçamento por fase antes de gastar é o que separa obras concluídas de obras paradas.',
  'mao-de-obra':
    'Revisar cláusulas de risco antes de assinar contrato evita disputas que atrasam a obra em meses.',
  tributos:
    'Regularizar corretamente desde o início garante o Habite-se e evita multas retroativas.',
  execucao:
    'O cronograma de decisões evita a obra parar por falta de material ou decisão no momento crítico.',
}

const INITIAL: DashboardData = {
  config: null,
  progressoGeral: 0,
  progressoPorModulo: [],
  totalGasto: 0,
  saldo: 0,
  pctUsado: 0,
  saudeFinanceira: 'ok',
  proximoPasso: null,
  proximoPassoInfo: null,
  dica: getDicaDoDia(),
  atividadeRecente: [],
  totalAlertas: 0,
}

export function useDashboard() {
  const [data, setData] = useState<DashboardData>(INITIAL)

  const load = useCallback(async () => {
    const [config, topicosAll, lancamentos, diarioEntries, alertas] = await Promise.all([
      getObraConfig(),
      db.topicos_progresso.toArray(),
      getLancamentos(),
      getDiario(),
      getAlertas(true),
    ])

    const progressoPorModulo: ModuloProgresso[] = MODULOS.map((m) => {
      const concluidos = topicosAll.filter((t) => t.moduloId === m.id && t.concluido).length
      const total = MODULO_TOTAL_TOPICOS[m.id] ?? 0
      const pct = total > 0 ? Math.round((concluidos / total) * 100) : 0
      return { id: m.id, titulo: m.titulo, icone: m.icone, concluidos, total, pct }
    })

    const totalConcluidos = topicosAll.filter((t) => t.concluido).length
    const progressoGeral =
      TOTAL_TOPICOS > 0 ? Math.round((totalConcluidos / TOTAL_TOPICOS) * 100) : 0

    const totalGasto = lancamentos
      .filter((l) => l.status === 'pago')
      .reduce((s, l) => s + l.valor, 0)
    const orcamento = config?.orcamentoTotal ?? 0
    const saldo = orcamento - totalGasto
    const pctUsado = orcamento > 0 ? Math.round((totalGasto / orcamento) * 100) : 0
    const saudeFinanceira: SaudeFinanceira =
      pctUsado >= 90 ? 'critico' : pctUsado >= 70 ? 'atencao' : 'ok'

    const incompletos = progressoPorModulo.filter((m) => m.pct < 100)
    let proximoPasso: string | null = null
    let proximoPassoInfo: ProximoPassoInfo | null = null
    if (incompletos.length > 0) {
      const mod = incompletos[0]!
      proximoPasso = `Continue em ${mod.titulo} (${mod.pct}% deste módulo)`

      const concluidosIds = new Set(
        topicosAll.filter((t) => t.moduloId === mod.id && t.concluido).map((t) => t.topicoId),
      )
      const moduloContent = modulosData.modulos.find((m) => m.id === mod.id)
      const primeiroTopico = moduloContent?.topicos.find((t) => !concluidosIds.has(t.id)) ?? null

      proximoPassoInfo = {
        moduloId: mod.id,
        moduloTitulo: mod.titulo,
        moduloIcone: mod.icone,
        topicoId: primeiroTopico?.id ?? null,
        topicoTitulo: primeiroTopico?.titulo ?? null,
        topicoPath: primeiroTopico ? topicoDetailPath(mod.id, primeiroTopico.id) : null,
        porque: PORQUE_POR_MODULO[mod.id] ?? 'Continue avançando na sua obra.',
        texto: proximoPasso,
      }
    }

    const atividades: AtividadeItem[] = [
      ...lancamentos.slice(0, 10).map((l) => ({
        tipo: 'lancamento' as const,
        descricao: l.titulo,
        contexto: `Financeiro · ${l.categoria}${l.tipo === 'receita' ? ' · receita' : ''}`,
        data: l.criadoEm,
      })),
      ...diarioEntries.slice(0, 10).map((d) => ({
        tipo: 'diario' as const,
        descricao: `${d.texto.slice(0, 70)}${d.texto.length > 70 ? '…' : ''}`,
        contexto: d.topicoId ? 'Diário · etapa vinculada' : 'Diário de bordo',
        data: d.criadoEm,
      })),
    ]
    atividades.sort((a, b) => b.data.localeCompare(a.data))

    setData({
      config: config ?? null,
      progressoGeral,
      progressoPorModulo,
      totalGasto,
      saldo,
      pctUsado,
      saudeFinanceira,
      proximoPasso,
      proximoPassoInfo,
      dica: getDicaDoDia(),
      atividadeRecente: atividades.slice(0, 5),
      totalAlertas: alertas.length,
    })
  }, [])

  useEffect(() => {
    load()
  }, [load])

  return { ...data, reload: load }
}
